import { useMemo } from 'react';
import Card from '../../../components/ui/Card';
import { IconLeaf } from '@tabler/icons-react';

// Read-only port of Ops src/components/MachineCarbon.js: same factors, same
// calculation, no edit controls. Keep the factor table in step with Ops.
const FACTOR_SOURCE = 'DESNZ UK greenhouse gas conversion factors';
const FACTOR_YEAR = 2024;

const FUELS = {
  gas_oil:      { label: 'Red diesel (gas oil)', kgPerLitre: 2.758 },
  diesel:       { label: 'Diesel (road)',        kgPerLitre: 2.512 },
  petrol:       { label: 'Petrol',               kgPerLitre: 2.340 },
  hvo:          { label: 'HVO',                  kgPerLitre: 0.038, note: 'Combustion emissions only; HVO is treated as largely biogenic.' },
  electric:     { label: 'Electric',             kgPerLitre: 0 },
};

const DEFAULT_LITRES_PER_HOUR = {
  tractor: 12,
  telehandler: 9,
  excavator: 10,
  loader: 11,
  mower: 5,
  sprayer: 8,
  harvester: 30,
  utv: 3,
};

const fmt = (n, dp = 0) =>
  n === null || n === undefined || isNaN(n)
    ? '—'
    : n.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });

// Callable returns ISO strings; tolerate a Timestamp-like just in case.
const toMs = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  const ms = Date.parse(v);
  return isNaN(ms) ? null : ms;
};

function Stat({ label, value, unit, sub }) {
  return (
    <div style={{ flex: 1, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 17, color: 'var(--color-text-primary)', marginTop: 3 }}>
        {value}
        {value !== '—' && <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginLeft: 4 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function MachineCarbonReadOnly({ machine, checks = [] }) {
  const defaultRate = DEFAULT_LITRES_PER_HOUR[machine?.subCategory] || null;
  const rate = machine?.fuelLitresPerHour ?? defaultRate;
  const fuel = FUELS[machine?.fuelType || 'gas_oil'] || FUELS.gas_oil;
  const isAssumed = machine?.fuelLitresPerHour === undefined || machine?.fuelLitresPerHour === null;

  // Hours worked in the last twelve months, from the spread of engine-hour
  // readings on submitted checks. Falls back to nothing rather than guessing.
  const hoursLastYear = useMemo(() => {
    const cutoff = Date.now() - 365 * 86400000;
    const readings = checks
      .map(c => ({
        at: toMs(c.submittedAt),
        hours: typeof c.engineHours === 'number' ? c.engineHours : null,
      }))
      .filter(r => r.at && r.hours !== null && r.at >= cutoff)
      .sort((a, b) => a.at - b.at);

    if (readings.length < 2) return null;
    const delta = readings[readings.length - 1].hours - readings[0].hours;
    return delta > 0 ? delta : null;
  }, [checks]);

  const totalHours = typeof machine?.currentHours === 'number' ? machine.currentHours : null;

  const lifetimeKg = rate && totalHours !== null ? rate * totalHours * fuel.kgPerLitre : null;
  const yearKg = rate && hoursLastYear !== null ? rate * hoursLastYear * fuel.kgPerLitre : null;
  const perHourKg = rate ? rate * fuel.kgPerLitre : null;

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <IconLeaf size={15} color="var(--color-success)" stroke={1.5} />
        <div style={{ fontSize: 14, fontWeight: 400 }}>Estimated carbon</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Stat label="Last 12 months" value={yearKg === null ? '—' : fmt(yearKg / 1000, 2)} unit="t CO2e"
              sub={hoursLastYear === null ? 'Not enough check data' : `${fmt(hoursLastYear)} h worked`} />
        <Stat label="Lifetime" value={lifetimeKg === null ? '—' : fmt(lifetimeKg / 1000, 2)} unit="t CO2e"
              sub={totalHours === null ? 'No hours recorded' : `${fmt(totalHours)} h total`} />
        <Stat label="Per hour" value={perHourKg === null ? '—' : fmt(perHourKg, 1)} unit="kg CO2e"
              sub={fuel.label} />
      </div>

      {!rate && (
        <div style={{ fontSize: 12, color: 'var(--color-warning)', marginBottom: 8 }}>
          No fuel consumption figure for this machine.
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', lineHeight: 1.6 }}>
        Estimated from engine hours at {rate ? `${rate} litres per hour` : 'an unknown rate'}
        {isAssumed && rate ? ' (a category assumption, not set by the customer)' : ''}, using{' '}
        {FACTOR_SOURCE} {FACTOR_YEAR}. This is an estimate from recorded hours, not measured fuel use.
        {fuel.note ? ' ' + fuel.note : ''}
      </div>
    </Card>
  );
}
